import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { each } from 'bluebird';
import PropTypes from 'prop-types';
import * as ramda from 'ramda';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Popover } from 'reactstrap';
import { v4 as uuid } from 'uuid';
import classnames from 'classnames';
import { kebabCase } from 'lodash';
import { keysSortedByValueAscending } from '@truqc/content-path';
import Label from '../label';
import { enabledSelector, labelSelector, newPathSelector, optionsSelector } from '../../selectors/index';
import entityComposition from '../../../common/classes/entityComposition';
import { evaluateSource } from '../../../../../behaviors/src/lib/evaluateSource';
import { configBehaviorsFunc } from '../../../../nativeBehaviors/configBehaviors';
import { nativeBehaviorEvaluator } from '../../../../nativeBehaviors/nativeBehaviorEvaluator';
import store from '../../../../reducers/storeRegistration';
import OptionActionsButtonBody from './optionActionsButtonBody';
import OptionActionsButtonHeader from './optionActionsButtonHeader';
import { getContent } from '../../selectors/simpleSelectors';
const getTransientContext = (data, filterText) => {
  const transientContext = {};
  if (data) {
    transientContext.selection = _objectSpread({}, data);
    // set transient context for value and path
    transientContext.value = data;
  }

  // searching
  transientContext.filterText = filterText;
  return transientContext;
};
const filterOption = option => {
  let newOption;
  if (option !== null && option !== undefined && typeof option === 'object' && Object.keys(option).length > 0) {
    newOption = _objectSpread({}, option);
    // remove the cruft we added on the option
    /* eslint-disable no-underscore-dangle */
    delete newOption._entity;
    delete newOption.identifier;
    delete newOption._isSelected;
    delete newOption._crumbs;
    /* eslint-enable no-underscore-dangle */

    // convert the object-style options to an array
    Object.keys(option).forEach(key => {
      if (typeof option[key] === 'object' && option[key] !== null) {
        newOption[key] = filterOption(option[key]);
      }
    });
  }
  return newOption;
};
const transformDeferredOptions = options => {
  if (Array.isArray(options)) {
    return options;
  }
  const optionKeys = options ? keysSortedByValueAscending(options) : [];
  // convert the object-style options to an array
  return optionKeys.map(id => {
    const option = options[id];
    return _objectSpread(_objectSpread({}, option), {}, {
      identifier: id
    });
  });
};
const OptionActionsButton = _ref => {
  let active = _ref.active,
    _ref$allowsMultipleSe = _ref.allowsMultipleSelection,
    allowsMultipleSelection = _ref$allowsMultipleSe === void 0 ? false : _ref$allowsMultipleSe,
    _ref$allowsOther = _ref.allowsOther,
    allowsOther = _ref$allowsOther === void 0 ? false : _ref$allowsOther,
    _ref$allowsParentSele = _ref.allowsParentSelection,
    allowsParentSelection = _ref$allowsParentSele === void 0 ? false : _ref$allowsParentSele,
    _ref$contentPath = _ref.contentPath,
    contentPath = _ref$contentPath === void 0 ? null : _ref$contentPath,
    _ref$deferredOptions = _ref.deferredOptions,
    deferredOptions = _ref$deferredOptions === void 0 ? null : _ref$deferredOptions,
    enabled = _ref.enabled,
    _ref$label = _ref.label,
    label = _ref$label === void 0 ? null : _ref$label,
    _ref$options = _ref.options,
    options = _ref$options === void 0 ? [] : _ref$options,
    path = _ref.path,
    _ref$postActions = _ref.postActions,
    actions = _ref$postActions === void 0 ? [] : _ref$postActions,
    reduxEntity = _ref.reduxEntity,
    title = _ref.title,
    _ref$variables = _ref.variables,
    variables = _ref$variables === void 0 ? {} : _ref$variables;
  const _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    popoverOpen = _useState2[0],
    setPopoverOpen = _useState2[1];
  const _useState3 = useState("oab-".concat(uuid())),
    _useState4 = _slicedToArray(_useState3, 1),
    buttonID = _useState4[0];
  const _useState5 = useState([]),
    _useState6 = _slicedToArray(_useState5, 2),
    selected = _useState6[0],
    setSelected = _useState6[1];
  const _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    initialLoadingDeferredOptions = _useState8[0],
    setInitialLoadingDeferredOptions = _useState8[1];
  const _useState9 = useState(false),
    _useState0 = _slicedToArray(_useState9, 2),
    loadingDeferredOptions = _useState0[0],
    setLoadingDeferredOptions = _useState0[1];
  const _useState1 = useState([]),
    _useState10 = _slicedToArray(_useState1, 2),
    evaluatedOptions = _useState10[0],
    setEvaluatedOptions = _useState10[1];
  const _useState11 = useState(null),
    _useState12 = _slicedToArray(_useState11, 2),
    evaluatedTitle = _useState12[0],
    setEvaluatedTitle = _useState12[1];
  const _useState13 = useState({}),
    _useState14 = _slicedToArray(_useState13, 2),
    selectionTree = _useState14[0],
    setSelectionTree = _useState14[1];
  const _useState15 = useState(null),
    _useState16 = _slicedToArray(_useState15, 2),
    filterText = _useState16[0],
    setFilterText = _useState16[1];
  const nativeFunctions = useMemo(() => {
    const evaluator = nativeBehaviorEvaluator.bind(null, active, reduxEntity);
    return {
      errorHandler: err => {
        /* eslint-disable-next-line no-console */
        console.log('error', err);
        throw err;
      },
      evaluator,
      configBehaviors: configBehaviorsFunc
    };
  }, [active, reduxEntity]);

  // when the popover is closed and selection(s) are made, fire the component's `postActions`
  const handleActions = useCallback(async tree => {
    const transientContext = getTransientContext(tree);
    transientContext.path = path;
    const option = filterOption(transientContext);
    // evaluate postActions
    await each(actions, async action => {
      // ensure we're using the latest content from the redux store
      const _store$getState$redux = store.getState()[reduxEntity].active,
        _store$getState$redux2 = _store$getState$redux === void 0 ? {} : _store$getState$redux,
        updatedContent = _store$getState$redux2.content;
      let parentPath = path;
      if (contentPath !== null && contentPath.length > 0) {
        parentPath = parentPath.substring(0, parentPath.length - contentPath.length - 1);
      }
      await evaluateSource(action, updatedContent, parentPath, _objectSpread(_objectSpread({}, option), variables), nativeFunctions);
    });
  }, [actions, nativeFunctions, variables, path, reduxEntity, contentPath]);

  // evaluate the `deferredOptions` behavior(s) and transform the results for usable options
  const evaluateDeferredOptions = useCallback(async function (newSelection) {
    let incomingFilterText = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    setLoadingDeferredOptions(true);
    let tree;
    if (newSelection) {
      tree = {
        [newSelection.identifier]: _objectSpread({}, newSelection)
      };
    }
    const transientContext = getTransientContext(tree, incomingFilterText);

    // ensure we're using the latest content from the redux store
    const _store$getState$redux3 = store.getState()[reduxEntity].active,
      _store$getState$redux4 = _store$getState$redux3 === void 0 ? {} : _store$getState$redux3,
      updatedContent = _store$getState$redux4.content;
    const newEvaluatedOptions = await evaluateSource(deferredOptions, updatedContent, path, _objectSpread(_objectSpread({}, transientContext), variables), nativeFunctions);
    const newOptions = transformDeferredOptions(newEvaluatedOptions.options);
    setEvaluatedOptions(newOptions);
    setEvaluatedTitle(newEvaluatedOptions.title || ' ');
    setLoadingDeferredOptions(false);
  }, [deferredOptions, nativeFunctions, path, variables, reduxEntity]);

  // set new state when user clicks back ("<") in the PopoverHeader
  const onBackSelect = useCallback(async () => {
    const updatedSelected = ramda.slice(0, -1, selected);
    setSelected(updatedSelected);
    if (deferredOptions) {
      let selection;
      if (updatedSelected.length > 0) {
        selection = updatedSelected[updatedSelected.length - 1];
      }
      await evaluateDeferredOptions(selection);
    }
    setFilterText(null);
  }, [deferredOptions, evaluateDeferredOptions, selected]);

  // control Popover visibility status and take necessary actions on close.
  const toggle = useCallback(async singleSelectValue => {
    setPopoverOpen(!popoverOpen);
    if (popoverOpen) {
      /* eslint-disable-next-line no-undef */
      const isMouseEvent = singleSelectValue instanceof MouseEvent;
      if (allowsMultipleSelection && Object.keys(selectionTree).length > 0) {
        // multiple options have been selected, handleActions
        await handleActions(selectionTree);
      } else if (!allowsMultipleSelection
      // when popover is not explicitly passed an argument, we get a MouseEvent. Ignore it.
      && !isMouseEvent && singleSelectValue && Object.keys(singleSelectValue).length === 1) {
        // we have made a single selectionTree, handleActions
        await handleActions(singleSelectValue);
      }
      // cleanup on popover close
      setSelected([]);
      setSelectionTree({});
      setEvaluatedOptions([]);
      setEvaluatedTitle(null);
      setFilterText(null);
      setInitialLoadingDeferredOptions(false);
      setLoadingDeferredOptions(false);
    }
  }, [allowsMultipleSelection, handleActions, popoverOpen, selectionTree]);
  const handleSearch = useCallback(async incomingValue => {
    const value = incomingValue === '' ? null : incomingValue;
    if (!(value === null && filterText === null)) {
      if (deferredOptions) {
        await evaluateDeferredOptions(selected[selected.length - 1], value);
      }
      setFilterText(value);
    }
  }, [deferredOptions, evaluateDeferredOptions, filterText, selected]);
  useEffect(() => {
    if (deferredOptions && popoverOpen && !initialLoadingDeferredOptions && !loadingDeferredOptions) {
      // we should arrive here when the popover first opens to trigger evaluateDeferredOptions
      setInitialLoadingDeferredOptions(true);
      evaluateDeferredOptions();
    }
  }, [deferredOptions, evaluateDeferredOptions, initialLoadingDeferredOptions, loadingDeferredOptions, popoverOpen]);
  const classNames = useMemo(() => classnames('tqc-control', "t-oab-".concat(kebabCase(title))), [title]);
  return /*#__PURE__*/React.createElement("div", null, label !== null ? /*#__PURE__*/React.createElement(Label, {
    value: label
  }) : undefined, /*#__PURE__*/React.createElement(Button, {
    id: buttonID,
    block: true,
    disabled: !enabled,
    type: "button",
    className: classNames
  }, title), /*#__PURE__*/React.createElement(Popover, {
    placement: "bottom",
    isOpen: popoverOpen,
    style: {
      width: '250px'
    },
    target: buttonID,
    toggle: toggle,
    trigger: "legacy"
  }, /*#__PURE__*/React.createElement(OptionActionsButtonHeader, {
    evaluateDeferredOptions: evaluateDeferredOptions,
    evaluatedTitle: evaluatedTitle,
    filterText: filterText,
    handleSearch: handleSearch,
    onBackSelect: onBackSelect,
    selected: selected
  }), /*#__PURE__*/React.createElement(OptionActionsButtonBody, {
    allowsMultipleSelection: allowsMultipleSelection,
    allowsParentSelection: allowsParentSelection,
    allowsOther: allowsOther,
    deferredOptions: deferredOptions,
    evaluatedOptions: evaluatedOptions,
    evaluateDeferredOptions: evaluateDeferredOptions,
    filterText: filterText,
    handleSearch: handleSearch,
    loadingDeferredOptions: loadingDeferredOptions,
    options: options,
    selected: selected,
    selectionTree: selectionTree,
    setFilterText: setFilterText,
    setSelected: setSelected,
    setSelectionTree: setSelectionTree,
    toggle: toggle
  })));
};
OptionActionsButton.propTypes = {
  allowsOther: PropTypes.bool,
  variables: PropTypes.shape({}),
  allowsMultipleSelection: PropTypes.bool,
  allowsParentSelection: PropTypes.bool,
  active: PropTypes.shape({}).isRequired,
  deferredOptions: PropTypes.shape({}),
  enabled: PropTypes.bool.isRequired,
  label: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({})),
  path: PropTypes.string.isRequired,
  postActions: PropTypes.arrayOf(PropTypes.shape({})),
  reduxEntity: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  contentPath: PropTypes.string
};
export { OptionActionsButton as UncomposedOptionActionButton };
export default entityComposition(OptionActionsButton, [], {
  enabled: enabledSelector,
  label: labelSelector,
  options: optionsSelector,
  path: newPathSelector,
  content: getContent,
  active: (state, _ref2) => {
    let reduxEntity = _ref2.reduxEntity;
    return state[reduxEntity].active;
  }
}, []);